import React, { useEffect, useRef } from "react";

import { z } from "zod";

import { Icon } from "@braze/beacon-core";
import { useId } from "@braze/beacon-hooks";

import { useAgenticAiContext } from "@services/useAgenticAiContext";
import { useCanUseOperator } from "@services/useCanUseOperator";
import useI18nNamespace from "@services/useI18nNamespace";
import { useIsAgenticAiChatOpen } from "@services/useIsAgenticAiChatOpen";
import { useOperatorAgenticChangeEnabled } from "@services/useOperatorAgenticChangeEnabled";

import { DiffDisplay } from "@components/AgenticAi/components/DiffDisplay/DiffDisplay";

import { StyledBrazeModal } from "./BrazeModal.style";
import type { BrazeModalProps } from "./types";

export const BrazeModal = ({
  isOpen,
  onOperatorClose,
  operatorLabel,
  operatorDisabledReason,
  children,
  size = "sm",
  ignoreOutsideClick,
  toggle,
  hideCloseButton,
  ...props
}: BrazeModalProps): React.ReactElement => {
  const { t } = useI18nNamespace("braze-modal");
  const { registerContext, registerTool } = useAgenticAiContext();
  const isOperatorChatOpen = useIsAgenticAiChatOpen();
  const canUseOperator = useCanUseOperator();
  const isAgenticChangeEnabled = useOperatorAgenticChangeEnabled();
  const id = useId("braze-modal");

  const onOperatorCloseRef = useRef(onOperatorClose ?? toggle);
  onOperatorCloseRef.current = onOperatorClose ?? toggle;

  useEffect(() => {
    if (!isOpen || !isAgenticChangeEnabled || hideCloseButton || operatorDisabledReason !== undefined) {
      return;
    }

    const cleanup = registerTool({
      toolEntityId: id,
      name: "close_modal",
      description:
        "Close the matching open modal. Identify the target from read_current_page using its modal content and data-operator-id.",
      parameters: z.object({}),
      callback: () => {
        onOperatorCloseRef.current?.();
        return "Modal was successfully closed.";
      },
      options: {
        toolGroup: "Other",
        labels: {
          ready: t("close-modal-ready", "Preparing to close modal"),
          running: t("close-modal-running", "Closing modal"),
          complete: t("close-modal-complete", "Modal closed"),
          failed: t("close-modal-failed", "Failed to close modal"),
          stopped: t("close-modal-stopped", "Stopped closing modal"),
        },
        requiresApproval: "yes",
        argumentsSummaryText: () => operatorLabel,
        renderApproval: () => {
          return (
            <DiffDisplay
              title={t("action-title", "Action")}
              modified={t("close-modal-description", "Close modal: {label}", { label: operatorLabel })}
            />
          );
        },
        toolIcon: <Icon name="times" />,
      },
    });

    return () => {
      cleanup();
    };
  }, [registerTool, id, t, isOpen, operatorLabel, operatorDisabledReason, isAgenticChangeEnabled, hideCloseButton]);

  useEffect(() => {
    if (!isOpen || !canUseOperator) {
      return;
    }

    return registerContext("braze_modal", "", { ignoreDuplicates: true });
  }, [canUseOperator, isOpen, registerContext]);

  return (
    <StyledBrazeModal
      isOpen={isOpen}
      size={size}
      ignoreOutsideClick={ignoreOutsideClick || isOperatorChatOpen}
      toggle={toggle}
      hideCloseButton={hideCloseButton}
      {...props}
      data-operator-id={id}
    >
      {children}
    </StyledBrazeModal>
  );
};
